/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Popover, PopoverActions, PopoverOrigin } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { QSTypography } from '../../../../../../shared/components/atoms';
import OverflowTooltip from '../../../../../../shared/components/molecules/overflow-tooltip';
import { customFieldConditionsAtom } from '../../../states/chat-list';
import { EntityTypes } from '../../../typings';
import { customFieldPickerAnchorAtom, customFieldSelectLabelsAtom, customFieldValuePickerAtom } from './state';
import { useFilterMetadata } from './hooks/use-filter-metadata';
import { CustomFieldCondition, CustomFieldConditionValue, CustomFieldOperatorMetadata } from './typings';
import { defaultValueForShape, filterCompleteConditions, preserveValueForOperator } from './utils';
import DataTypeIcon, { dataTypeLabel } from './data-type-icon';
import OperatorSelect from './operator-select';
import TextValueInput from './value-inputs/text-value-input';
import NumberValueInput from './value-inputs/number-value-input';
import DateValueInput from './value-inputs/date-value-input';
import BooleanValueInput from './value-inputs/boolean-value-input';
import SelectValueInput from './value-inputs/select-value-input';

const PAPER_WIDTH = 300;
const PAPER_MAX_HEIGHT = 400;

const isSingleSelectMode = (operator: string): boolean => operator === 'EQUALS' || operator === 'NOT_EQUALS';

const ValuePickerPopover = () => {
  const valuePicker = useAtomValue(customFieldValuePickerAtom);
  const setValuePicker = useSetAtom(customFieldValuePickerAtom);
  const setPickerAnchor = useSetAtom(customFieldPickerAnchorAtom);
  const conditions = useAtomValue(customFieldConditionsAtom);
  const setConditions = useSetAtom(customFieldConditionsAtom);
  const setSelectLabels = useSetAtom(customFieldSelectLabelsAtom);
  const { requestData: metadataRequest } = useFilterMetadata(false);

  const field = useMemo(() => {
    if (!valuePicker) return null;
    return (metadataRequest.data?.fields ?? []).find((f) => f.id === valuePicker.fieldId) ?? null;
  }, [valuePicker, metadataRequest.data?.fields]);

  const operators: CustomFieldOperatorMetadata[] = useMemo(() => {
    if (!field) return [];
    return metadataRequest.data?.dataTypeOperator?.[field.dataType] ?? [];
  }, [field, metadataRequest.data?.dataTypeOperator]);

  const existing = useMemo(() => {
    if (!valuePicker || valuePicker.editIndex < 0) return null;
    return conditions[valuePicker.editIndex] ?? null;
  }, [valuePicker, conditions]);

  const initialOperator = useMemo<CustomFieldOperatorMetadata | null>(() => {
    if (existing) {
      const match = operators.find((o) => o.type === existing.operator);
      if (match) return match;
    }
    return operators[0] ?? null;
  }, [existing, operators]);

  const [operator, setOperator] = useState<CustomFieldOperatorMetadata | null>(initialOperator);
  const [value, setValue] = useState<CustomFieldConditionValue>(() => {
    if (existing) return existing.value;
    const prefill = valuePicker?.prefill;
    if (prefill && initialOperator) {
      if (initialOperator.valueType === prefill.type) return prefill;
      if (
        initialOperator.valueType === 'BETWEEN' &&
        prefill.type === 'VALUE' &&
        (typeof prefill.value === 'string' || typeof prefill.value === 'number')
      ) {
        return { type: 'BETWEEN', start: prefill.value, end: '' };
      }
    }
    return defaultValueForShape(initialOperator?.valueType ?? 'VALUE', initialOperator?.suffix);
  });
  const [origins, setOrigins] = useState<{ anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin }>({
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  });
  const popoverActionRef = useRef<PopoverActions | null>(null);
  const [paperEl, setPaperEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!paperEl) return;
    const ro = new ResizeObserver(() => {
      popoverActionRef.current?.updatePosition();
    });
    ro.observe(paperEl);
    return () => ro.disconnect();
  }, [paperEl]);

  useLayoutEffect(() => {
    const anchorEl = valuePicker?.anchor;
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.top;
    const flipUp = spaceBelow < PAPER_MAX_HEIGHT;
    setOrigins(
      flipUp
        ? {
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
          }
        : {
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }
    );
  }, [valuePicker?.anchor]);

  const activeOperator = operator ?? initialOperator;

  if (!valuePicker || !field || !activeOperator) {
    return null;
  }

  const handleOperatorChange = (next: CustomFieldOperatorMetadata) => {
    setOperator(next);
    setValue((prev) => preserveValueForOperator(prev, next));
  };

  const handleClose = () => {
    setValuePicker(null);
    setOperator(null);
  };

  const handleApply = () => {
    const candidate: CustomFieldCondition = {
      id: field.id,
      type: field.dataType,
      operator: activeOperator.type,
      value,
    };
    const valid = filterCompleteConditions([candidate]).length > 0;
    if (!valid) return;
    if (valuePicker.editIndex >= 0) {
      setConditions({ type: 'replace', index: valuePicker.editIndex, condition: candidate });
    } else {
      setConditions({ type: 'add', condition: candidate });
    }
    setValuePicker(null);
    setOperator(null);
    setPickerAnchor(null);
  };

  const recordSelectLabels = (fieldId: string, labels: { value: string; label: string }[]) => {
    setSelectLabels((prev) => {
      const existingLabels = prev[fieldId] ?? [];
      const known = new Set(existingLabels.map((l) => l.value));
      const additions = labels.filter((l) => !known.has(l.value));
      if (additions.length === 0) return prev;
      return { ...prev, [fieldId]: [...existingLabels, ...additions] };
    });
  };

  const renderValueInput = () => {
    const shape = activeOperator.valueType;
    if (shape === 'NO_VALUE') return null;
    if (field.dataType === 'text') return <TextValueInput value={value} onChange={setValue} />;
    if (field.dataType === 'number') return <NumberValueInput shape={shape} value={value} onChange={setValue} />;
    if (field.dataType === 'date')
      return <DateValueInput shape={shape} suffixes={activeOperator.suffix} value={value} onChange={setValue} />;
    if (field.dataType === 'boolean') return <BooleanValueInput value={value} onChange={setValue} />;
    if (field.dataType === 'single_select' || field.dataType === 'multi_select') {
      return (
        <SelectValueInput
          fieldId={field.id}
          fieldName={field.name}
          value={value}
          singleSelectMode={field.dataType === 'single_select' && isSingleSelectMode(activeOperator.type)}
          onChange={(next, knownLabels) => {
            setValue(next);
            recordSelectLabels(field.id, knownLabels);
          }}
        />
      );
    }
    return null;
  };

  const isApplyEnabled =
    filterCompleteConditions([{ id: field.id, type: field.dataType, operator: activeOperator.type, value }]).length > 0;

  return (
    <Popover
      open={Boolean(valuePicker)}
      anchorEl={valuePicker.anchor}
      onClose={handleClose}
      anchorOrigin={origins.anchorOrigin}
      transformOrigin={origins.transformOrigin}
      action={popoverActionRef}
      PaperProps={{
        ref: (node: HTMLElement | null) => setPaperEl(node),
        sx: {
          width: PAPER_WIDTH,
          maxHeight: PAPER_MAX_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '8px',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          padding: '0px',
          border: '1px solid rgba(30, 41, 59, 0.14)',
        },
      }}
    >
      <Box
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          padding: 12px 12px 12px 12px;
          flex-shrink: 0;
        `}
      >
        <QSTypography
          css={css`
            font-size: 14px;
            font-weight: 600;
            color: #2d2d2d;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
            min-width: 0;
          `}
        >
          <OverflowTooltip>{`${field.name}`}</OverflowTooltip>
        </QSTypography>
        <Box
          css={css`
            display: flex;
            align-items: center;
            gap: 4px;
            flex-shrink: 0;
          `}
        >
          <DataTypeIcon dataType={field.dataType} size={16} />
          <QSTypography
            css={css`
              font-size: 12px;
              color: #7a7a7a;
            `}
          >
            {dataTypeLabel(field.dataType)}
          </QSTypography>
          {field.entityType && (
            <>
              <Box
                css={css`
                  width: 3px;
                  height: 3px;
                  background: #7a7a7a;
                  border-radius: 50%;
                `}
              />
              <QSTypography
                css={css`
                  font-size: 12px;
                  color: #7a7a7a;
                `}
              >
                {field.entityType === EntityTypes.CHAT ? 'Channel level' : 'Customer level'}
              </QSTypography>
            </>
          )}
        </Box>
      </Box>
      <Box
        css={css`
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        `}
      >
        <Box
          css={css`
            margin-bottom: 12px;
            padding: 0px 12px;
            flex-shrink: 0;
          `}
        >
          <OperatorSelect value={activeOperator.type} operators={operators} onChange={handleOperatorChange} />
        </Box>
        <Box
          css={css`
            margin-bottom: 16px;
            padding: ${field.dataType === 'single_select' || field.dataType === 'multi_select'
              ? '0px'
              : '0px 12px 0px 12px'};
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          `}
        >
          {renderValueInput()}
        </Box>
      </Box>
      <Box
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 4px 12px 12px 12px;
          flex-shrink: 0;
        `}
      >
        <Button variant="text" onClick={handleClose} sx={{ textTransform: 'none', color: '#2d2d2d', fontWeight: 500 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isApplyEnabled}
          onClick={handleApply}
          sx={{
            textTransform: 'none',
            background: '#3f906d',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { background: '#317657', boxShadow: 'none' },
          }}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default memo(ValuePickerPopover);
